import {
  XYCHART_DOWN_COLOR,
  XYCHART_NEUTRAL_COLOR,
  XYCHART_SERIES_COLORS,
  XYCHART_UP_COLOR,
} from './xychart-constants';
import {
  XYCHART_SERIES_TYPE,
  type XYChartParsed,
  type XYChartSeriesType,
} from './xychart-types';

const ARROW_UP = '▲';
const ARROW_DOWN = '▼';
const ARROW_NEUTRAL = '·';

enum XYChartCompactTrendDirection {
  Up = 'up',
  Down = 'down',
  Neutral = 'neutral',
}

interface XYChartNumberFormatterRule {
  matches: (absoluteValue: number) => boolean;
  format: (input: XYChartNumberFormatterInput) => string;
}

interface XYChartNumberFormatterInput {
  value: number;
  absoluteValue: number;
}

const HERO_NUMBER_FORMATTERS: readonly XYChartNumberFormatterRule[] = [
  {
    matches: (absoluteValue) => absoluteValue >= 1_000_000,
    format: ({ value }) => `${(value / 1_000_000).toFixed(2)}M`,
  },
  {
    matches: (absoluteValue) => absoluteValue >= 100_000,
    format: ({ value }) => `${(value / 1_000).toFixed(1)}k`,
  },
  {
    matches: (absoluteValue) => absoluteValue >= 1_000,
    format: ({ value }) => value.toLocaleString(undefined, { maximumFractionDigits: 0 }),
  },
  {
    matches: () => false,
    format: ({ value }) => value.toLocaleString(),
  },
];

const DELTA_VALUE_FORMATTERS: readonly XYChartNumberFormatterRule[] = [
  {
    matches: (absoluteValue) => absoluteValue >= 1_000_000,
    format: ({ absoluteValue }) => `${(absoluteValue / 1_000_000).toFixed(2)}M`,
  },
  {
    matches: (absoluteValue) => absoluteValue >= 1_000,
    format: ({ absoluteValue }) =>
      absoluteValue.toLocaleString(undefined, { maximumFractionDigits: 0 }),
  },
  {
    matches: () => false,
    format: ({ absoluteValue }) => absoluteValue.toLocaleString(),
  },
];

const SERIES_TYPE_BY_INPUT: Record<XYChartSeriesType, XYChartSeriesType> = {
  [XYCHART_SERIES_TYPE.Bar]: XYCHART_SERIES_TYPE.Bar,
  [XYCHART_SERIES_TYPE.Line]: XYCHART_SERIES_TYPE.Line,
};

const TREND_BY_DIRECTION: Record<
  XYChartCompactTrendDirection,
  { arrow: string; color: string; sign: string }
> = {
  [XYChartCompactTrendDirection.Up]: {
    arrow: ARROW_UP,
    color: XYCHART_UP_COLOR,
    sign: '+',
  },
  [XYChartCompactTrendDirection.Down]: {
    arrow: ARROW_DOWN,
    color: XYCHART_DOWN_COLOR,
    sign: '−',
  },
  [XYChartCompactTrendDirection.Neutral]: {
    arrow: ARROW_NEUTRAL,
    color: XYCHART_NEUTRAL_COLOR,
    sign: '',
  },
};

export interface XYChartPreviewSeries {
  seriesIndex: number;
  label: string;
  color: string;
  type: XYChartSeriesType;
  data: number[];
}

export interface XYChartCompactTrend {
  arrow: string;
  color: string;
  sign: string;
}

export interface XYChartCompactPreviewModel {
  title: string | null;
  focusSeries: XYChartPreviewSeries;
  fadedSeries: XYChartPreviewSeries[];
  latestValue: number;
  latestCategoryLabel: string;
  delta: number;
  deltaPercent: number;
  trend: XYChartCompactTrend;
}

interface SelectXYChartCompactPreviewModelInput {
  parsed: XYChartParsed;
}

export function selectXYChartCompactPreviewModel({
  parsed,
}: SelectXYChartCompactPreviewModelInput): XYChartCompactPreviewModel | null {
  const previewSeries = selectXYChartPreviewSeries({ parsed });
  const focusSeries = previewSeries[0];

  if (!focusSeries || parsed.xAxis.categories.length === 0) {
    return null;
  }

  const latestIndex = focusSeries.data.length - 1;
  const latestValue = focusSeries.data[latestIndex] ?? 0;
  const previousValue = focusSeries.data[latestIndex - 1] ?? latestValue;
  const delta = latestValue - previousValue;
  const deltaPercent =
    previousValue !== 0 ? (delta / Math.abs(previousValue)) * 100 : delta === 0 ? 0 : delta;

  return {
    title: parsed.title,
    focusSeries,
    fadedSeries: previewSeries.slice(1),
    latestValue,
    latestCategoryLabel: parsed.xAxis.categories[latestIndex] ?? `#${latestIndex + 1}`,
    delta,
    deltaPercent,
    trend: selectXYChartCompactTrend({ delta }),
  };
}

interface SelectXYChartPreviewSeriesInput {
  parsed: XYChartParsed;
}

function selectXYChartPreviewSeries({
  parsed,
}: SelectXYChartPreviewSeriesInput): XYChartPreviewSeries[] {
  return parsed.series.map((series) => ({
    seriesIndex: series.colorIndex,
    label: series.label,
    color: XYCHART_SERIES_COLORS[series.colorIndex % XYCHART_SERIES_COLORS.length],
    type: SERIES_TYPE_BY_INPUT[series.type],
    data: series.data,
  }));
}

interface SelectXYChartCompactTrendInput {
  delta: number;
}

function selectXYChartCompactTrend({
  delta,
}: SelectXYChartCompactTrendInput): XYChartCompactTrend {
  return TREND_BY_DIRECTION[selectXYChartCompactTrendDirection({ delta })];
}

function selectXYChartCompactTrendDirection({
  delta,
}: SelectXYChartCompactTrendInput): XYChartCompactTrendDirection {
  if (delta > 0) return XYChartCompactTrendDirection.Up;
  if (delta < 0) return XYChartCompactTrendDirection.Down;
  return XYChartCompactTrendDirection.Neutral;
}

export function formatHeroNumber(value: number): string {
  const absoluteValue = Math.abs(value);
  if (Number.isInteger(value) && absoluteValue < 1_000) {
    return value.toLocaleString();
  }
  const rule = HERO_NUMBER_FORMATTERS.find((formatter) => formatter.matches(absoluteValue));
  if (rule) {
    return rule.format({ value, absoluteValue });
  }
  return value.toFixed(2);
}

export function formatDeltaValue(value: number): string {
  const absoluteValue = Math.abs(value);
  if (Number.isInteger(absoluteValue) && absoluteValue < 1_000) {
    return absoluteValue.toLocaleString();
  }
  const rule = DELTA_VALUE_FORMATTERS.find((formatter) => formatter.matches(absoluteValue));
  if (rule) {
    return rule.format({ value, absoluteValue });
  }
  return absoluteValue.toFixed(2);
}

export function formatDeltaPercent(percent: number): string {
  const absoluteValue = Math.abs(percent);
  if (absoluteValue < 0.05) return '0.0%';
  if (absoluteValue < 10) return `${absoluteValue.toFixed(2)}%`;
  if (absoluteValue < 100) return `${absoluteValue.toFixed(1)}%`;
  return `${Math.round(absoluteValue)}%`;
}
